import React from "react";
import { css } from "@linaria/core";
import { renderToString } from "react-dom/server";
import capitalize from "../../../lib/capitalize";
import Text from "@ui/Text";
import { colors } from "@ui/Theme";
import { locale } from "../../../i18n/locale";

export type ReportingTooltipValue = {
  name: string;
  color?: string;
  value: number | string | null;
  unit?: string;
  maximumFractionDigits?: number;
};

const ReportingTooltipContent = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ReportingTooltipTextContainer = css`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 24px;

  span:first-child {
    display: inline-flex;
    align-items: center;
    color: ${colors.carbon700};
    gap: 4px;
  }

  span:last-child {
    justify-self: end;
  }
`;

export function useReportingTooltipRender() {
  return React.useCallback(
    ({
      title,
      subtitle,
      values,
    }: {
      title?: string;
      subtitle?: string;
      values: ReportingTooltipValue[][];
    }) =>
      renderToString(
        <div className={ReportingTooltipContent}>
          {title && <Text type="body1SemiBold">{capitalize(title)}</Text>}
          {subtitle && <Text type="body2">{subtitle}</Text>}
          {values.map((entries, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${title}-${i}`}>
              {entries.map(({ name, color, value, unit, maximumFractionDigits = 1 }) =>
                value ? (
                  <p key={name} className={ReportingTooltipTextContainer}>
                    <Text as="span" type="body2SemiBold">
                      {/* Highchart will complain of invalid svg. But it is totally correct. */}
                      {color && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 5 5"
                          width="5"
                          height="5"
                        >
                          <circle cx="2.5" cy="2.5" r="2.5" fill={color} />
                        </svg>
                      )}{" "}
                      {name}
                    </Text>
                    <Text as="span" type="body2SemiBold">
                      {typeof value === "number"
                        ? Intl.NumberFormat(locale, { maximumFractionDigits }).format(value)
                        : value}{" "}
                      {unit}
                    </Text>
                  </p>
                ) : null,
              )}
            </div>
          ))}
        </div>,
      ),
    [],
  );
}
